import { Link } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { TranslationKeys } from '@/lib/translations/types';
import type { FileRouteTypes } from '@/routeTree.gen';

export const ScanningStatusText = ({
  scanPercentage,
}: {
  scanPercentage: number;
}): JSX.Element => <span>Scanning... ({scanPercentage}%)</span>;

export const WarningsTooltip = ({
  warnings,
  connected,
}: {
  warnings: TranslationKeys[];
  connected: boolean;
}): JSX.Element | null => {
  if (warnings.length === 0 || !connected) {
    return null;
  }

  return (
    <TooltipProvider disableHoverableContent>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <Info className="h-3 w-3 inline mb-[2px] ml-1 text-gray-500 cursor-pointer stroke-[3px]" />
        </TooltipTrigger>
        <TooltipContent side="right">
          <div className="flex flex-col gap-2">
            {warnings.map(warning => (
              <p key={warning} className="text-xs">
                <FormattedMessage id={warning} />
              </p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const ScanProgressBar = ({
  isScanning,
  scanPercentage,
}: {
  isScanning: boolean;
  scanPercentage?: number;
}): JSX.Element | null => {
  if (!isScanning) {
    return null;
  }

  return (
    <div className="w-full overflow-hidden transition-all duration-500 ease-in-out px-2">
      <Progress value={scanPercentage} className="h-1" />
    </div>
  );
};

export const IntegrationButton = ({
  to,
  available,
  onClick,
  title,
  formatMessage,
  connected = false,
}: {
  to?: FileRouteTypes['to'];
  available: boolean;
  onClick?: () => void;
  title: TranslationKeys;
  formatMessage: (descriptor: { id: TranslationKeys }) => string;
  connected?: boolean;
}): JSX.Element => {
  const buttonContent = (
    <Button
      variant="main"
      disabled={!available}
      className="w-full rounded-sm bg-gray-200 hover:bg-gray-300 text-gray-800 text-xs dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 h-7 py-1"
      onClick={onClick}
    >
      {connected ? (
        <FormattedMessage id="integrations.tryIt.configure" />
      ) : (
        <FormattedMessage
          id="integrations.tryIt.import"
          values={{ value: formatMessage({ id: title }) }}
        />
      )}
    </Button>
  );

  if (to !== undefined) {
    return (
      <Link to={to} className="w-full">
        {buttonContent}
      </Link>
    );
  }

  return buttonContent;
};
